(() => {
  const saved = new Map();
  const ns = 'http://www.w3.org/2000/svg';
  const presets = [[2, 3], [3, 4], [4, 6], [5, 8], [6, 9], [8, 10], [9, 12]];
  const near = (a, b) => Math.abs(a - b) < 0.001;
  const positive = n => Number.isFinite(n) && n > 0;
  const number = n => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {maximumFractionDigits: 2}).format(n);
  const fill = (s, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll('[' + key + ']', String(value)), s);
  const parseSize = label => {
    const clean = String(label).toLowerCase().replaceAll('×', 'x').replaceAll('feet', 'ft').replaceAll('foot', 'ft');
    const parts = clean.match(/(\d+(?:\.\d+)?)\s*(?:ft|cm|m|in|')?\s*x\s*(\d+(?:\.\d+)?)/);
    if (!parts) return null;
    const factor = /cm/.test(clean) ? 1 / 30.48 : /\bm\b/.test(clean) ? 100 / 30.48 : /\bin\b/.test(clean) ? 1 / 12 : 1;
    const a = Number(parts[1]) * factor;
    const b = Number(parts[2]) * factor;
    return positive(a) && positive(b) ? {w: Math.min(a, b), l: Math.max(a, b)} : null;
  };
  const labelSize = (w, l, shape, unit, text) => {
    const scale = unit === 'cm' ? 30.48 : 1;
    const suffix = unit === 'cm' ? text.unit_cm : text.unit_ft;
    return shape === 'round' ? number(w * scale) + ' ' + suffix + ' ' + text.diameter.toLowerCase() : number(w * scale) + ' × ' + number(l * scale) + ' ' + suffix;
  };
  const canonical = (w, l) => ({w: Math.min(w, l), l: Math.max(w, l)});
  const elem = (tag, attrs, text) => {
    const e = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs || {})) e.setAttribute(k, String(v));
    if (text !== undefined) e.textContent = text;
    return e;
  };
  const svgNode = (tag, attrs, text) => {
    const e = document.createElementNS(ns, tag);
    for (const [k, v] of Object.entries(attrs || {})) e.setAttribute(k, String(v));
    if (text !== undefined) e.textContent = text;
    return e;
  };

  class RugSizing extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.config = JSON.parse(this.querySelector('[data-sizing-config]').textContent);
      this.text = this.config.text;
      this.formId = this.config.formId;
      this.nativeShape = this.config.shape;
      this.values = this.config.values.map(v => ({...v, dims: parseSize(v.label)})).filter(v => v.dims);
      this.fields = this.querySelector('[data-custom-fields]');
      this.widthInput = this.querySelector('[data-width]');
      this.lengthInput = this.querySelector('[data-length]');
      this.state = saved.get(this.formId);
      const selected = this.values.find(v => v.selected) || this.values.find(v => v.available) || this.values[0];
      if (!this.state) {
        const params = new URL(location.href).searchParams;
        const requestedShape = params.get('rug_shape');
        const shapes = ['rectangle', 'square', 'round', 'runner', this.nativeShape];
        const requestedW = Number(params.get('rug_width_ft'));
        const requestedL = Number(params.get('rug_length_ft'));
        this.state = {
          shape: shapes.includes(requestedShape) ? requestedShape : this.nativeShape,
          unit: params.get('rug_unit') === 'cm' ? 'cm' : 'ft',
          custom: !!requestedShape || !selected,
          w: positive(requestedW) ? requestedW : selected ? selected.dims.w : null,
          l: positive(requestedL) ? requestedL : selected ? selected.dims.l : null,
          optionId: selected ? selected.id : null
        };
      } else if (!this.state.custom && selected) {
        this.state.w = selected.dims.w;
        this.state.l = selected.dims.l;
        this.state.optionId = selected.id;
      }
      saved.set(this.formId, this.state);
      this.onClick = event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.dataset.shape) this.selectShape(button.dataset.shape);
        if (button.dataset.unit) this.setUnit(button.dataset.unit);
        if (button.dataset.sizeIndex !== undefined) this.chooseDimensions(button._dims.w, button._dims.l, this.state.shape);
        if (button.hasAttribute('data-custom-choice')) this.setCustom();
        if (button.hasAttribute('data-quote-open')) this.openQuote();
        if (button.hasAttribute('data-quote-close')) this.querySelector('dialog').close();
      };
      this.addEventListener('click', this.onClick);
      this.onInput = () => {
        const factor = this.state.unit === 'cm' ? 1 / 30.48 : 1;
        this.state.w = this.widthInput.value === '' ? null : Number(this.widthInput.value) * factor;
        this.state.l = this.state.shape === 'round' || this.state.shape === 'square' ? this.state.w : this.lengthInput.value === '' ? null : Number(this.lengthInput.value) * factor;
        this.state.custom = true;
        this.renderSummary();
        this.persist();
      };
      this.widthInput.addEventListener('input', this.onInput);
      this.lengthInput.addEventListener('input', this.onInput);
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.observer = new MutationObserver(() => this.syncCommerce());
      this.observer.observe(this.section, {childList: true, subtree: true});
      this.querySelector('[data-sizing-controls]').hidden = false;
      this.dataset.ready = 'true';
      this.render();
      const dialog = this.querySelector('dialog');
      if (dialog && dialog.hasAttribute('data-status')) {
        dialog.removeAttribute('open');
        if (dialog.querySelector('[data-quote-summary]') && this.valid()) this.openQuote();
        else dialog.showModal();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.removeEventListener('click', this.onClick);
      this.widthInput?.removeEventListener('input', this.onInput);
      this.lengthInput?.removeEventListener('input', this.onInput);
      this.initialised = false;
    }

    valid() {
      return positive(this.state.w) && positive(this.state.l) &&
        (this.state.shape !== 'square' || near(this.state.w, this.state.l)) &&
        (this.state.shape !== 'round' || near(this.state.w, this.state.l)) &&
        (this.state.shape !== 'runner' || this.state.l + 0.001 >= this.state.w * 2);
    }

    matching(w, l, shape) {
      if (!positive(w) || !positive(l)) return null;
      const c = canonical(w, l);
      const compatible = shape === this.nativeShape || shape === 'runner' && this.nativeShape === 'rectangle' && l + 0.001 >= w * 2;
      if (!compatible) return null;
      return this.values.find(v => near(v.dims.w, c.w) && near(v.dims.l, c.l)) || null;
    }

    availableSizes() {
      if (this.state.shape === 'round' && this.nativeShape === 'round') return this.values.map(v => ({w: v.dims.w, l: v.dims.w}));
      if (this.state.shape === 'square' && this.nativeShape === 'square') return this.values.map(v => ({w: v.dims.w, l: v.dims.l}));
      if (this.state.shape === 'runner') return this.nativeShape === 'rectangle' || this.nativeShape === 'runner' ? this.values.filter(v => v.dims.l >= 2 * v.dims.w).map(v => v.dims) : [];
      if (this.state.shape !== this.nativeShape) return [];
      if (this.state.shape === 'round' || this.state.shape === 'square') return [];
      const all = [...presets.map(([w, l]) => ({w, l})), ...this.values.map(v => v.dims)];
      return all.filter((v, i) => all.findIndex(a => near(a.w, v.w) && near(a.l, v.l)) === i).sort((a, b) => a.w * a.l - b.w * b.l || a.w - b.w);
    }

    selectShape(shape) {
      this.state.shape = shape;
      this.state.optionId = null;
      if (shape === this.nativeShape) {
        const selectedId = this.querySelector('[data-native-picker] input[data-option-position="' + this.config.position + '"]:checked')?.value;
        const selected = this.values.find(v => String(v.id) === String(selectedId)) || this.values.find(v => v.selected) || this.values.find(v => v.available);
        if (selected) {
          this.chooseDimensions(selected.dims.w, selected.dims.l, shape);
          return;
        }
      }
      this.state.custom = true;
      this.state.w = null;
      this.state.l = null;
      this.persist();
      this.render();
    }

    setUnit(unit) {
      this.state.unit = unit;
      this.persist();
      this.render();
    }

    setCustom() {
      this.state.custom = true;
      this.state.optionId = null;
      this.persist();
      this.render();
      this.widthInput.focus();
    }

    chooseDimensions(w, l, shape) {
      this.state.shape = shape;
      this.state.w = w;
      this.state.l = l;
      const value = this.matching(w, l, shape);
      this.state.custom = !value;
      this.state.optionId = value ? value.id : null;
      this.persist();
      this.render();
      if (value) {
        const input = this.querySelector('[data-native-picker] input[data-option-position="' + this.config.position + '"][value="' + value.id + '"]');
        if (input && !input.checked) input.click();
      }
    }

    persist() {
      saved.set(this.formId, this.state);
      const url = new URL(location.href);
      if (this.state.custom) {
        url.searchParams.set('rug_shape', this.state.shape);
        for (const [key, value] of [['rug_width_ft', this.state.w], ['rug_length_ft', this.state.l]]) {
          if (positive(value)) url.searchParams.set(key, String(value));
          else url.searchParams.delete(key);
        }
      } else {
        ['rug_shape', 'rug_width_ft', 'rug_length_ft'].forEach(key => url.searchParams.delete(key));
      }
      if (this.state.unit === 'cm') url.searchParams.set('rug_unit', 'cm');
      else url.searchParams.delete('rug_unit');
      history.replaceState(history.state, '', url);
      this.syncCommerce();
      this.dispatchEvent(new CustomEvent('indigenite:size-selected', {bubbles: true, detail: {...this.state, productId: this.config.productId}}));
    }

    render() {
      this.querySelectorAll('[data-shape]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.shape === this.state.shape)));
      this.querySelectorAll('[data-unit]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.unit === this.state.unit)));
      const list = this.querySelector('[data-size-options]');
      list.replaceChildren();
      this.availableSizes().forEach((dims, index) => {
        const value = this.matching(dims.w, dims.l, this.state.shape);
        const selected = !this.state.custom && value && value.id === this.state.optionId;
        const button = elem('button', {type: 'button', 'data-size-index': index, 'aria-pressed': selected, class: 'ind-rug-size-chip'});
        button._dims = dims;
        button.append(elem('span', {}, labelSize(dims.w, dims.l, this.state.shape, this.state.unit, this.text)));
        if (!value) button.append(elem('small', {class: 'ind-rug-size-chip__quote'}, this.text.quote_badge));
        if (value && !value.available) {
          button.disabled = true;
          button.append(elem('small', {}, this.text.sold_out));
        }
        list.append(button);
      });
      list.hidden = list.childElementCount === 0;
      this.querySelector('[data-custom-choice]').setAttribute('aria-pressed', String(this.state.custom));
      this.fields.hidden = !this.state.custom;
      const oneSide = this.state.shape === 'round' || this.state.shape === 'square';
      this.querySelector('[data-length-field]').hidden = oneSide;
      this.lengthInput.disabled = oneSide;
      const widthLabel = this.state.shape === 'round' ? this.text.diameter : this.state.shape === 'square' ? this.text.side_length : this.state.shape === 'irregular' ? this.text.maximum_width : this.text.width;
      this.querySelector('[data-width-label]').textContent = widthLabel + ' (' + (this.state.unit === 'cm' ? this.text.unit_cm : this.text.unit_ft) + ')';
      this.querySelector('[data-length-label]').textContent = (this.state.shape === 'irregular' ? this.text.maximum_length : this.text.length) + ' (' + (this.state.unit === 'cm' ? this.text.unit_cm : this.text.unit_ft) + ')';
      const factor = this.state.unit === 'cm' ? 30.48 : 1;
      this.widthInput.value = positive(this.state.w) ? String(Math.round(this.state.w * factor * 100) / 100) : '';
      this.lengthInput.value = positive(this.state.l) ? String(Math.round(this.state.l * factor * 100) / 100) : '';
      this.querySelector('[data-shape-note]').textContent = this.state.shape !== this.nativeShape ? this.text.adaptation_note : this.state.shape === 'irregular' ? this.text.outline_note : this.text.size_note;
      this.renderSummary();
      this.syncCommerce();
      this.dispatchEvent(new CustomEvent('indigenite:size-selected', {bubbles: true, detail: {...this.state, productId: this.config.productId}}));
    }

    renderSummary() {
      const valid = this.valid();
      const summary = this.querySelector('[data-size-summary]');
      summary.textContent = valid ? labelSize(this.state.w, this.state.l, this.state.shape, this.state.unit, this.text) : this.text.choose_dimensions;
      const area = this.state.shape === 'round' ? Math.PI * this.state.w * this.state.w / 4 : this.state.shape === 'oval' ? Math.PI * this.state.w * this.state.l / 4 : this.state.w * this.state.l;
      this.querySelector('[data-area]').textContent = valid && this.state.shape !== 'irregular' ? fill(this.text.area, {area: number(area)}) : '';
      let error = '';
      if ((this.widthInput.value !== '' && !positive(this.state.w)) || (this.lengthInput.value !== '' && !positive(this.state.l))) error = this.text.positive_error;
      if (positive(this.state.w) && positive(this.state.l) && this.state.shape === 'runner' && this.state.l + 0.001 < this.state.w * 2) error = this.text.runner_error;
      this.querySelector('[data-dimension-error]').textContent = error;
      this.widthInput.setAttribute('aria-invalid', String(!!error));
      this.lengthInput.setAttribute('aria-invalid', String(!!error));
      this.querySelector('[data-quote-open]').disabled = !valid;
      const selected = this.values.find(v => v.id === this.state.optionId);
      const priced = !this.state.custom && !this.state.pending && selected;
      const label = valid ? labelSize(this.state.w, this.state.l, this.state.shape, 'ft', this.text) + ' / ' + labelSize(this.state.w, this.state.l, this.state.shape, 'cm', this.text) : '';
      this.querySelector('[data-cart-shape]').value = this.text['shape_' + this.state.shape] || this.state.shape;
      this.querySelector('[data-cart-dimensions]').value = label;
      this.querySelector('[data-cart-shape]').disabled = !priced;
      this.querySelector('[data-cart-dimensions]').disabled = !priced;
    }

    syncCommerce() {
      if (!this.section || !this.state) return;
      this.querySelectorAll('[data-native-picker] input[data-option-position="' + this.config.position + '"]').forEach(input => {
        const fieldset = input.closest('fieldset');
        if (fieldset) fieldset.hidden = true;
      });
      const expected = this.values.find(value => value.id === this.state.optionId);
      const variantScript = this.querySelector('[data-native-picker] script[data-variant]');
      let nativeVariant = null;
      try {nativeVariant = JSON.parse(variantScript?.textContent || 'null');} catch {}
      this.state.pending = !this.state.custom && !!expected && nativeVariant?.options?.[this.config.position - 1] !== expected.label;
      this.section.toggleAttribute('data-ir-pending', this.state.pending);
      this.setAttribute('aria-busy', String(this.state.pending));
      const progress = this.querySelector('[data-sizing-progress]');
      if (progress) progress.hidden = !this.state.pending;
      this.querySelectorAll('[data-cart-shape],[data-cart-dimensions]').forEach(input => {input.disabled = this.state.custom || this.state.pending || !expected;});
      if (this.state.custom) this.section.setAttribute('data-ir-quote', 'true');
      else this.section.removeAttribute('data-ir-quote');
    }

    openQuote() {
      if (!this.valid()) return;
      const dialog = this.querySelector('dialog');
      const form = dialog.closest('form');
      const returnInput = form?.querySelector('[name="return_to"]');
      if (returnInput) {
        const returnUrl = new URL(location.href);
        returnUrl.hash = form.getAttribute('id');
        returnInput.value = returnUrl.pathname + returnUrl.search + returnUrl.hash;
      }
      const shape = this.text['shape_' + this.state.shape] || this.state.shape;
      const size = labelSize(this.state.w, this.state.l, this.state.shape, 'ft', this.text) + ' / ' + labelSize(this.state.w, this.state.l, this.state.shape, 'cm', this.text);
      dialog.querySelector('[data-quote-summary]').textContent = this.config.title + ' · ' + shape + ' · ' + size;
      dialog.querySelector('[name="contact[Requested shape]"]').value = shape;
      dialog.querySelector('[name="contact[Requested dimensions]"]').value = size;
      const body = dialog.querySelector('[name="contact[body]"]');
      if (!body.value || body.dataset.generated === 'true') {
        body.value = fill(this.text.quote_message, {product: this.config.title, shape, size, url: this.config.productUrl});
        body.dataset.generated = 'true';
        body.addEventListener('input', () => {body.dataset.generated = 'false';}, {once: true});
      }
      dialog.showModal();
    }
  }

  document.addEventListener('submit', event => {
    const formId = event.target.getAttribute('id');
    const state = saved.get(formId);
    if (state && (state.custom || state.pending)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      document.querySelector('indigenite-rug-sizing[data-form-id="' + CSS.escape(formId) + '"]')?.querySelector('[data-custom-fields]')?.scrollIntoView({block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }
  }, true);

  const rooms = {
    living: {width: 18, height: 16, cx: 9, cy: 7, sizes: [[5, 8], [6, 9], [8, 10], [9, 12], [10, 14], [12, 15]], layouts: ['sofa_small', 'sofa_standard', 'sofa_sectional']},
    dining: {width: 18, height: 16, cx: 9, cy: 8, sizes: [[6, 9], [8, 10], [9, 12]], layouts: ['table_four', 'table_six', 'table_eight', 'table_round']},
    bedroom: {width: 14, height: 18, cx: 7, cy: 8.5, sizes: [[5, 8], [6, 9], [8, 10], [9, 12], [2.5, 8, 'runner']], layouts: ['bed_single', 'bed_queen', 'bed_king']},
    kitchen: {width: 8, height: 14, cx: 4, cy: 7, sizes: [[2, 3], [2, 5, 'runner'], [2.5, 7, 'runner'], [2.5, 10, 'runner']], layouts: ['kitchen']}
  };

  const rugContains = (x, y, cx, cy, horizontal, vertical, shape) => {
    if (shape === 'round' || shape === 'oval') return ((x - cx) / (horizontal / 2)) ** 2 + ((y - cy) / (vertical / 2)) ** 2 <= 1;
    return Math.abs(x - cx) <= horizontal / 2 + 0.01 && Math.abs(y - cy) <= vertical / 2 + 0.01;
  };

  function drawPlan(roomKey, layout, w, l, shape, text, mini = false) {
    const room = rooms[roomKey];
    const svg = svgNode('svg', {viewBox: '-2 -2 ' + (room.width + 4) + ' ' + (room.height + 4), class: 'ind-rug-plan', preserveAspectRatio: 'xMidYMid meet', role: mini ? 'presentation' : 'img'});
    if (mini) svg.setAttribute('aria-hidden', 'true');
    else svg.append(svgNode('title', {}, text['room_' + roomKey] + ' · ' + labelSize(w, l, shape, 'ft', text)));
    const floor = svgNode('g', {fill: 'none', stroke: '#d7d0c4', 'stroke-width': 0.035});
    floor.append(svgNode('rect', {x: 0, y: 0, width: room.width, height: room.height, rx: 0.12}));
    for (let x = 2; x < room.width; x += 2) floor.append(svgNode('line', {x1: x, y1: 0, x2: x, y2: room.height, opacity: 0.28}));
    for (let y = 2; y < room.height; y += 2) floor.append(svgNode('line', {x1: 0, y1: y, x2: room.width, y2: y, opacity: 0.28}));
    svg.append(floor);
    const horizontal = roomKey === 'kitchen' ? w : l;
    const vertical = roomKey === 'kitchen' ? l : w;
    const rug = svgNode('g', {fill: '#c9b68f', 'fill-opacity': 0.5, stroke: '#8b6e44', 'stroke-width': 0.07});
    const addRug = (cx, cy, rw, rh) => {
      if (shape === 'round' || shape === 'oval') rug.append(svgNode('ellipse', {cx, cy, rx: rw / 2, ry: rh / 2}));
      else rug.append(svgNode('rect', {x: cx - rw / 2, y: cy - rh / 2, width: rw, height: rh, rx: 0.05, ...(shape === 'irregular' ? {'stroke-dasharray': '0.2 0.15', fill: 'none'} : {})}));
    };
    const bedW = layout === 'bed_single' ? 3 : layout === 'bed_queen' ? 5 : 6;
    if (roomKey === 'bedroom' && shape === 'runner') {
      addRug(7 - bedW / 2 - 0.5 - w / 2, 6.5, w, l);
      addRug(7 + bedW / 2 + 0.5 + w / 2, 6.5, w, l);
    } else addRug(room.cx, room.cy, horizontal, vertical);
    svg.append(rug);
    const furniture = svgNode('g', {fill: '#faf8f3', stroke: '#585a50', 'stroke-width': 0.055});
    const feet = [];
    const rect = (x, y, width, height, radius = 0.12) => furniture.append(svgNode('rect', {x, y, width, height, rx: radius}));
    const line = (x1, y1, x2, y2) => furniture.append(svgNode('line', {x1, y1, x2, y2}));
    const chair = (x, y, width, height, allFeet = false) => {
      rect(x, y, width, height);
      rect(x + 0.15, y + 0.18, width - 0.3, height - 0.36, 0.08);
      if (allFeet) for (const px of [x + 0.15, x + width - 0.15]) for (const py of [y + 0.15, y + height - 0.15]) feet.push([px, py]);
    };
    let example = '';
    if (roomKey === 'living') {
      const sw = layout === 'sofa_small' ? 5 : layout === 'sofa_sectional' ? 9 : 7;
      const sx = 9 - sw / 2;
      rect(sx, 2, sw, 3);
      rect(sx + 0.18, 2.2, sw - 0.36, 0.5, 0.08);
      for (let i = 1; i < 3; i++) line(sx + sw * i / 3, 2.7, sx + sw * i / 3, 4.65);
      feet.push([sx + 0.5, 4.8], [sx + sw - 0.5, 4.8]);
      if (layout === 'sofa_sectional') {
        rect(sx, 5, 3, 3);
        feet.push([sx + 0.35, 7.8]);
        chair(13.4, 6, 3, 3);
        feet.push([13.55, 6.4], [13.55, 8.6]);
      } else {
        chair(2.5, 5.5, 3, 3);
        chair(12.5, 5.5, 3, 3);
        feet.push([5.3, 5.9], [5.3, 8.1], [12.7, 5.9], [12.7, 8.1]);
      }
      rect(7, 6.25, 4, 2, 0.2);
      example = text['layout_' + layout];
    } else if (roomKey === 'dining') {
      const tableW = layout === 'table_four' ? 4 : layout === 'table_eight' ? 8 : 6;
      const tableH = layout === 'table_four' ? 2.5 : 3;
      if (layout === 'table_round') {
        furniture.append(svgNode('circle', {cx: 9, cy: 8, r: 2}));
        for (const [x, y] of [[8.25, 4], [8.25, 10.5], [5, 7.25], [11.5, 7.25]]) chair(x, y, 1.5, 1.5, true);
      } else {
        rect(9 - tableW / 2, 8 - tableH / 2, tableW, tableH, 0.13);
        const perSide = layout === 'table_eight' ? 3 : 2;
        for (let i = 0; i < perSide; i++) {
          const x = 9 - tableW / 2 + tableW * (i + 0.5) / perSide - 0.75;
          chair(x, 8 - tableH / 2 - 2, 1.5, 1.5, true);
          chair(x, 8 + tableH / 2 + 0.5, 1.5, 1.5, true);
        }
        if (layout !== 'table_four') {
          chair(9 - tableW / 2 - 2, 7.25, 1.5, 1.5, true);
          chair(9 + tableW / 2 + 0.5, 7.25, 1.5, 1.5, true);
        }
      }
      example = text['layout_' + layout];
    } else if (roomKey === 'bedroom') {
      const x = 7 - bedW / 2;
      rect(x, 2.5, bedW, 6.5, 0.08);
      rect(x + 0.12, 2.65, bedW - 0.24, 1.25, 0.08);
      const pillows = bedW === 3 ? 1 : 2;
      for (let i = 0; i < pillows; i++) rect(x + 0.22 + (bedW - 0.44) * i / pillows, 2.85, (bedW - 0.7) / pillows, 0.8, 0.1);
      line(x + 0.12, 4.2, x + bedW - 0.12, 4.2);
      rect(x - 2.3, 2.5, 1.8, 1.8);
      rect(x + bedW + 0.5, 2.5, 1.8, 1.8);
      example = text['layout_' + layout];
    } else {
      rect(0, 0, 2.5, 12, 0);
      rect(5.5, 0, 2.5, 12, 0);
      rect(0.55, 2, 1.4, 2);
      furniture.append(svgNode('circle', {cx: 6.75, cy: 4, r: 0.7}));
      example = text.layout_kitchen;
    }
    svg.append(furniture);
    let inside = 0;
    for (const [x, y] of feet) {
      const fits = rugContains(x, y, room.cx, room.cy, horizontal, vertical, shape);
      if (fits) inside++;
      svg.append(svgNode('circle', {cx: x, cy: y, r: 0.085, fill: fits ? '#557461' : '#ad6b47'}));
    }
    if (!mini) {
      const dimension = svgNode('g', {stroke: '#8b6e44', 'stroke-width': 0.04, fill: '#4d4434', 'font-size': 0.65, 'text-anchor': 'middle'});
      if (!(roomKey === 'bedroom' && shape === 'runner')) {
        const left = room.cx - horizontal / 2;
        const right = room.cx + horizontal / 2;
        dimension.append(svgNode('line', {x1: left, y1: room.height + 0.65, x2: right, y2: room.height + 0.65}));
        for (const x of [left, right]) dimension.append(svgNode('line', {x1: x, y1: room.height + 0.5, x2: x, y2: room.height + 0.8}));
      }
      dimension.append(svgNode('text', {x: room.width / 2, y: room.height + 1.45, stroke: 'none'}, labelSize(w, l, shape, 'ft', text)));
      svg.append(dimension);
    }
    let feedback = '';
    if (roomKey === 'living') feedback = inside === feet.length ? text.living_fit : text.living_partial;
    if (roomKey === 'dining') feedback = inside === feet.length ? text.dining_fit : text.dining_partial;
    if (roomKey === 'bedroom') {
      if (shape === 'runner') feedback = text.runner_pair;
      else if (shape === 'round' || shape === 'oval' || shape === 'irregular') feedback = text.bedroom_shaped;
      else feedback = fill(text.bedroom_clearance, {clearance: number(Math.max(0, (l - bedW) / 2))});
    }
    if (roomKey === 'kitchen') feedback = w <= 3 ? text.kitchen_fit : text.kitchen_wide;
    if (horizontal > room.width || vertical > room.height) feedback = text.outside_example;
    return {svg, feedback, example};
  }

  class RugGuide extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.config = JSON.parse(this.querySelector('[data-guide-config]').textContent);
      this.text = this.config.text;
      this.room = 'living';
      this.layout = 'sofa_standard';
      this.unit = 'ft';
      this.shape = this.config.shape || 'rectangle';
      this.w = this.shape === 'round' ? 6 : 5;
      this.l = this.shape === 'round' ? 6 : 8;
      this.main = document.querySelector('indigenite-rug-sizing[data-product-id="' + this.config.productId + '"]');
      if (this.main?.state && positive(this.main.state.w) && positive(this.main.state.l)) this.readMain(this.main.state);
      this.onSelection = event => {
        if (String(event.detail.productId) !== String(this.config.productId)) return;
        this.main = event.target.closest('indigenite-rug-sizing') || this.main;
        if (positive(event.detail.w) && positive(event.detail.l)) this.readMain(event.detail);
        this.render();
      };
      document.addEventListener('indigenite:size-selected', this.onSelection);
      this.onClick = event => {
        const b = event.target.closest('button');
        if (!b || !this.contains(b)) return;
        if (b.dataset.room) {
          this.room = b.dataset.room;
          this.layout = rooms[this.room].layouts[0];
          if (this.room === 'dining' && this.shape === 'round') this.layout = 'table_round';
          this.render();
        }
        if (b.dataset.guideUnit) {
          this.unit = b.dataset.guideUnit;
          this.render();
        }
        if (b._dims) {
          this.w = b._dims.w;
          this.l = b._dims.l;
          this.shape = b._dims.shape;
          this.render();
        }
        if (b.hasAttribute('data-use-size')) {
          this.main = document.querySelector('indigenite-rug-sizing[data-product-id="' + this.config.productId + '"]');
          if (this.main) {
            this.main.chooseDimensions(this.w, this.l, this.shape);
            this.main.scrollIntoView({block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
          } else location.href = this.config.customUrl;
        }
      };
      this.addEventListener('click', this.onClick);
      this.layoutInput = this.querySelector('[data-guide-layout]');
      this.onLayout = () => {this.layout = this.layoutInput.value; this.render();};
      this.layoutInput.addEventListener('change', this.onLayout);
      this.querySelector('[data-guide-controls]').hidden = false;
      this.dataset.ready = 'true';
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('indigenite:size-selected', this.onSelection);
      this.removeEventListener('click', this.onClick);
      this.layoutInput?.removeEventListener('change', this.onLayout);
      this.initialised = false;
    }

    readMain(state) {
      this.w = state.w;
      this.l = state.l;
      this.shape = state.shape;
      this.unit = state.unit;
    }

    render() {
      this.querySelectorAll('[data-room]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.room === this.room)));
      this.querySelectorAll('[data-guide-unit]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.guideUnit === this.unit)));
      this.layoutInput.replaceChildren(...rooms[this.room].layouts.map(key => elem('option', {value: key}, this.text['layout_' + key])));
      this.layoutInput.value = this.layout;
      const drawing = drawPlan(this.room, this.layout, this.w, this.l, this.shape, this.text);
      this.querySelector('[data-plan]').replaceChildren(drawing.svg);
      this.querySelector('[data-guide-feedback]').textContent = drawing.feedback;
      this.querySelector('[data-guide-example]').textContent = this.text.example + ' ' + drawing.example;
      this.querySelector('[data-guide-dimensions]').textContent = labelSize(this.w, this.l, this.shape, this.unit, this.text);
      this.querySelector('[data-guide-other-dimensions]').textContent = labelSize(this.w, this.l, this.shape, this.unit === 'ft' ? 'cm' : 'ft', this.text);
      const guidance = this.config.guidance[this.room] || '';
      this.querySelector('[data-room-guidance]').textContent = guidance;
      const cards = this.querySelector('[data-comparison-cards]');
      cards.replaceChildren();
      let choices = rooms[this.room].sizes.map(([w, l, shape]) => ({w, l, shape: shape || this.shape}));
      if (this.shape === 'round') choices = (this.room === 'dining' ? [6, 8, 9, 10] : this.room === 'bedroom' ? [4, 6, 8, 9] : [3, 4, 6, 8, 9, 12]).map(d => ({w: d, l: d, shape: 'round'}));
      if (this.shape === 'square') choices = [4, 6, 8, 9, 12].map(d => ({w: d, l: d, shape: 'square'}));
      if (this.shape === 'runner' && this.room !== 'kitchen' && this.room !== 'bedroom') choices = [[2, 5], [2.5, 7], [2.5, 8], [2.5, 10]].map(([w, l]) => ({w, l, shape: 'runner'}));
      if (this.shape === 'runner' && this.room === 'bedroom') choices = [[2, 5], [2.5, 7], [2.5, 8], [2.5, 10]].map(([w, l]) => ({w, l, shape: 'runner'}));
      for (const dims of choices) {
        const selected = near(dims.w, this.w) && near(dims.l, this.l) && dims.shape === this.shape;
        const button = elem('button', {type: 'button', class: 'ind-rug-compare', 'aria-pressed': selected});
        button._dims = dims;
        const mini = drawPlan(this.room, this.layout, dims.w, dims.l, dims.shape, this.text, true);
        button.append(mini.svg, elem('span', {}, labelSize(dims.w, dims.l, dims.shape, this.unit, this.text)));
        const matched = this.main?.matching(dims.w, dims.l, dims.shape);
        button.append(elem('small', {}, matched ? matched.available ? this.text.listed_size : this.text.sold_out : this.text.custom_size));
        cards.append(button);
      }
      const allSizes = this.querySelector('[data-all-listed-sizes]');
      allSizes.replaceChildren();
      if (this.main) for (const value of this.main.values) {
        const button = elem('button', {type: 'button', class: 'ind-rug-listed'});
        button._dims = {w: value.dims.w, l: value.dims.l, shape: this.main.nativeShape};
        button.textContent = labelSize(value.dims.w, value.dims.l, this.main.nativeShape, this.unit, this.text);
        allSizes.append(button);
      }
      this.querySelector('[data-listed-wrap]').hidden = allSizes.childElementCount === 0;
      const matched = this.main?.matching(this.w, this.l, this.shape);
      this.querySelector('[data-use-size]').textContent = matched && matched.available ? this.text.use_size : this.text.request_size;
      this.querySelector('[data-use-size]').disabled = !!matched && !matched.available;
      this.querySelector('[data-guide-fit-note]').textContent = this.shape === 'irregular' ? this.text.irregular_note : this.text.measure_note;
    }
  }

  if (!customElements.get('indigenite-rug-sizing')) customElements.define('indigenite-rug-sizing', RugSizing);
  if (!customElements.get('indigenite-rug-size-guide')) customElements.define('indigenite-rug-size-guide', RugGuide);
})();
